import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import axios from "axios"
import { useSelector, useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { applyCoupon, createOrder } from "../../../store/Cab/CabAction"
import { cabAction } from "../../../store/Cab/CAbReducer"
import { useNavigate } from "react-router-dom"
import loadScript from "../../../utils/loadScript"
import { slugify } from "../../../utils/slugify"
import { trackGenerateLead } from "../../../utils/analytics"

const PaymentCard = ({ tripData }) => {
  const [paymentOption, setPaymentOption] = useState("complete")
  const [couponCode, setCouponCode] = useState("")
  const [isCouponApplied, setIsCouponApplied] = useState(false)
  const [showPromo, setShowPromo] = useState(false)
  const [offerAmount, setOfferAmount] = useState(0)
  const [timeLeft, setTimeLeft] = useState(600) // 10 minutes
  const autoCouponAppliedRef = useRef(false)
  const leadFiredRef = useRef(false)
  const navigate = useNavigate()

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const finalAmount = Number(parseInt(tripData?.cab?.total)) || 0
  const subAmount = Number(parseInt(tripData?.cab?.subtotal)) || 0
  const taxAmount = Number(parseInt(tripData?.cab?.gst)) || 0

  // Coupon API returns `discount: 10`, but for PULPIT10 we need 10% discount, not ₹10.
  // So all payment calculations use local percentage discount based on selected cab total.
  const payableAmount = Math.max(finalAmount - offerAmount, 0)
  const partialAmount = Number(parseInt(payableAmount * 0.2)) || 0

  const dispatch = useDispatch()
  const {
    couponApplied,
    couponAppliedSuccess,
    visitorEntryId,
    couponAppliedError,
    orderSuccess,
    order,
  } = useSelector((state) => state.cab)

  // Form completion check
  const [isFormComplete, setIsFormComplete] = useState(false)

  useEffect(() => {
    const checkForm = () => {
      const form = JSON.parse(localStorage.getItem("tripData")) || {}
      setIsFormComplete(
        !!(form.first_name && form.last_name && form.email && isValidEmail(form.email) && form.mobile_number && form.mobile_number.length === 10)
      )
    }
    checkForm()
    // Poll loosely for form changes since localstorage doesn't trigger renders across components natively without context
    const interval = setInterval(checkForm, 1000)
    return () => clearInterval(interval)
  }, [])

  // Timer logic
  useEffect(() => {
    if (timeLeft <= 0) return
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000)
    return () => clearInterval(timer)
  }, [timeLeft])

  // Redirect on expiry
  useEffect(() => {
    if (timeLeft === 0) {
      toast.error("Session Expired. Redirecting to vehicle selection...", {
        position: "top-center",
        autoClose: 3000,
      })

      const timer = setTimeout(() => {
        const routePath = localStorage.getItem("routePath")
        if (routePath) {
          navigate(`/routes/${routePath.replace(/\/$/, "")}/`)
        } else {
          navigate("/cabs")
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [timeLeft, navigate])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    if (couponAppliedSuccess) {
      setIsCouponApplied(true)
      setShowPromo(true)

      const discountValue = Number(couponApplied?.discount) || 0
      const isPercentCoupon = discountValue > 0 && discountValue <= 100
      const calculatedDiscount = isPercentCoupon
        ? Math.round((finalAmount * discountValue) / 100)
        : Math.round(discountValue)

      setOfferAmount(calculatedDiscount)
      dispatch(cabAction.clearCouponAppliedSuccess())
    }
    if (couponAppliedError) {
      toast.error("Invalid Promo Code")
      dispatch(cabAction.clearCouponAppliedError())
    }
  }, [couponAppliedSuccess, couponApplied, couponAppliedError, dispatch])

  useEffect(() => {
    if (!orderSuccess || !order?.order_id) return

    // A committed lead: contact info is captured and an order now exists. Fires
    // once, even if the rider abandons the Razorpay modal, so lead count is real.
    if (!leadFiredRef.current) {
      leadFiredRef.current = true
      trackGenerateLead({ value: finalAmount || undefined, currency: "INR", lead_id: order?.order_id })
    }

    const openRazorpay = async () => {
      try {
        await loadScript("https://checkout.razorpay.com/v1/checkout.js")
      } catch (err) {
        toast.error("Payment gateway could not be loaded. Please try again later.")
        return
      }
      if (!window.Razorpay) {
        toast.error("Razorpay is not loaded.")
        return
      }

      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: "INR",
        name: order.name || "Pulpit Cab",
        description: "Pulpit Cab Booking",
        order_id: order.order_id,
        handler: async function (response) {
          const savedTripData = JSON.parse(localStorage.getItem("tripData")) || {}
          const from = slugify(savedTripData.pickup_city || "")
          const to = savedTripData.drop_city ? slugify(savedTripData.drop_city) : null
          const successSlug = to && from ? `${from}-to-${to}` : `${from}-local`

          // In live mode createOrder returns no booking_id: the booking is only
          // created once the server verifies Razorpay's signature. Ask it to.
          let bookingId = order.booking_id || null
          if (!bookingId && response?.razorpay_signature) {
            try {
              const v = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/website/verify_payment`,
                {
                  visitorEntryId,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
                { headers: { "Content-Type": "application/json" } }
              )
              bookingId = v?.data?.booking_id || null
            } catch (err) {
              // The money is taken and the webhook is the backstop, so never
              // strand the customer on this screen, send them to the
              // confirmation page and let it show the trip reference.
              console.warn("[payment] verification call failed; webhook will confirm", err?.message)
            }
          }

          navigate(`/booking-success/${successSlug}`, {
            state: { tripData: savedTripData, booking_id: bookingId },
          })
        },
        prefill: {
          name: order.name || "",
          email: order.email || "",
          contact: order.contact || "",
        },
        theme: { color: "#21A5A0" },
      }

      const rzp = new window.Razorpay(options)
      rzp.on("payment.failed", function (response) {
        toast.error(`Payment failed: ${response.error?.description || "Unknown error"}`)
      })
      rzp.open()
    }
    openRazorpay()
  }, [orderSuccess, order])

  useEffect(() => {
    return () => {
      if (orderSuccess) dispatch(cabAction.clearOrderSuccess())
    }
  }, [orderSuccess, dispatch])

  const handleCouponApply = () => {
    if (!couponCode || !visitorEntryId) return
    const data = {
      visitorEntryId: visitorEntryId,
      coupon_for: tripData?.trip_type === 3 ? "OutStation" : tripData?.trip_type === 2 ? "Rental" : "Local",
      code: couponCode,
      grand_total: parseInt(tripData?.cab?.total),
    }
    dispatch(applyCoupon(data))
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const autoCoupon = params.get("coupon") || localStorage.getItem("autoCouponCode")

    if (!autoCoupon || !visitorEntryId || autoCouponAppliedRef.current || isCouponApplied) return

    autoCouponAppliedRef.current = true
    const code = autoCoupon.toUpperCase()
    setCouponCode(code)

    dispatch(
      applyCoupon({
        visitorEntryId: visitorEntryId,
        coupon_for: tripData?.trip_type === 3 ? "OutStation" : tripData?.trip_type === 2 ? "Rental" : "Local",
        code,
        grand_total: parseInt(tripData?.cab?.total),
      })
    )
  }, [visitorEntryId, isCouponApplied, tripData?.trip_type, tripData?.cab?.total, dispatch])

  const handleCouponRemove = () => {
    setIsCouponApplied(false)
    setOfferAmount(0)
    setCouponCode("")
  }

  const scrollToForm = () => {
    const el = document.getElementById("traveller-form")
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "center" })
    const input = el.querySelector("input")
    if (input) setTimeout(() => input.focus(), 450)
  }

  const handlePay = () => {
    if (!isFormComplete) {
      toast.error("Please fill the traveller information completely.")
      scrollToForm()
      return
    }
    const savedTripData = JSON.parse(localStorage.getItem("tripData"))
    const data = {
      visitorEntryId: visitorEntryId,
      first_name: savedTripData.first_name,
      last_name: savedTripData.last_name,
      email: savedTripData.email,
      phone_number: "+91" + savedTripData.mobile_number,
      payment_amount: payableAmount * 100,
      advance_booking: paymentOption === "complete" ? 100 : 20,
      advance_booking_amount: paymentOption === "complete" ? payableAmount * 100 : partialAmount * 100,
      GST: Number(parseInt(tripData?.cab?.gst_percentage)),
      GSTCalculate: Number(parseInt(tripData?.cab?.gst)),
      is_full_payment: paymentOption === "complete" ? 1 : 0,
    }
    dispatch(createOrder(data))
  }

  const dueNow = paymentOption === "complete" ? payableAmount : partialAmount

  return (
    <div className="bg-white rounded-3xl p-5 md:p-7 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-gray-50 flex flex-col font-body">
      {/* Timer Banner */}
      <div className="mb-5 rounded-xl py-2.5 px-4 bg-[#FFF6E9] text-[#FF8F00] flex justify-center items-center gap-2">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <span className="text-[12px] font-black tracking-wide uppercase">
          {timeLeft > 0 ? `BOOKING RESERVED FOR ${formatTime(timeLeft)}` : 'SESSION EXPIRED'}
        </span>
      </div>

      <h2 className="text-[19px] font-black text-[#1a1a1a] mb-4">
        Payment Summary
      </h2>

      {/* Payment Options */}
      <div className="space-y-2.5 mb-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setPaymentOption("complete")}
          className={`flex items-center justify-between p-3.5 rounded-xl border-2 cursor-pointer transition-all outline-none ${
          paymentOption === "complete"
            ? "border-[#21A5A0] bg-[#21A5A0]/[0.02]"
            : "border-[#e8edec] hover:border-[#d4edec] bg-[#fcfcfc]"
        }`}>
          <div className="flex items-center gap-3">
             <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${
                paymentOption === "complete" ? "border-[#21A5A0]" : "border-gray-300"
              }`}>
                {paymentOption === "complete" && <div className="w-2.5 h-2.5 rounded-full bg-[#21A5A0]"></div>}
              </div>
            <div className="font-bold text-[14px] text-[#1a1a1a]">Full payment</div>
          </div>
          <div className="text-[15px] font-black text-[#1a1a1a]">₹{payableAmount.toLocaleString()}</div>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setPaymentOption("partial")}
          className={`flex items-center justify-between p-3.5 rounded-xl border-2 cursor-pointer transition-all outline-none ${
          paymentOption === "partial"
            ? "border-[#21A5A0] bg-[#21A5A0]/[0.02]"
            : "border-[#e8edec] hover:border-[#d4edec] bg-[#fcfcfc]"
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${
              paymentOption === "partial" ? "border-[#21A5A0]" : "border-gray-300"
            }`}>
              {paymentOption === "partial" && <div className="w-2.5 h-2.5 rounded-full bg-[#21A5A0]"></div>}
            </div>
            <div>
              <div className="font-bold text-[14px] text-[#1a1a1a]">Pay 20% now</div>
              <div className="text-[12px] font-medium text-[#777]">
                Pay driver later: <span className="font-bold text-[#3d4946]">₹{(payableAmount - partialAmount).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="text-[15px] font-black text-[#1a1a1a]">₹{partialAmount.toLocaleString()}</div>
        </div>
      </div>

      {/* Price Details */}
      <div className="space-y-2 mb-3">
        <div className="flex justify-between text-[#555] font-medium text-[13.5px]">
          <span>Base Amount</span>
          <span className="font-bold text-[#1a1a1a]">₹{subAmount.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-[#555] font-medium text-[13.5px]">
          <span>Taxes &amp; Fees</span>
          <span className="font-bold text-[#1a1a1a]">₹{taxAmount.toLocaleString()}</span>
        </div>
        {isCouponApplied && (
          <div className="flex justify-between text-[#10B981] font-bold text-[13.5px]">
            <span>Discount</span>
            <span>- ₹{offerAmount.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Promo Code, collapsed by default to keep the summary short */}
      <div className="mb-4">
        {!isCouponApplied && !showPromo && (
          <button
            onClick={() => setShowPromo(true)}
            className="flex items-center gap-1.5 text-[13px] font-bold text-[#21A5A0] hover:text-[#178f89] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Have a promo code?
          </button>
        )}

        {!isCouponApplied && showPromo && (
          <div className="flex gap-2 h-[44px] w-full">
            <input
              type="text"
              value={couponCode}
              placeholder="PULPIT20"
              className="min-w-0 flex-1 px-4 rounded-xl bg-[#f4f7f6] border border-[#e8edec] outline-none text-[#1a1a1a] text-[14px] font-bold uppercase placeholder:normal-case placeholder:text-gray-400 placeholder:font-medium focus:border-[#21A5A0]"
              onChange={(e) => setCouponCode(e.target.value)}
              autoFocus
            />
            <button
              className="shrink-0 px-6 rounded-xl border-2 border-[#21A5A0] text-[#21A5A0] font-bold text-[14px] hover:bg-[#21A5A0] hover:text-white transition-colors"
              onClick={handleCouponApply}
            >
              Apply
            </button>
          </div>
        )}

        {isCouponApplied && (
          <div className="flex items-center justify-between p-3 px-4 bg-[#E8F5F4] border border-[#d4edec] rounded-xl h-[44px]">
            <div className="font-bold text-[#21A5A0] text-[14px]">Applied: {couponCode}</div>
            <button onClick={handleCouponRemove} className="text-[13px] font-bold text-red-500 hover:text-red-700">Remove</button>
          </div>
        )}
      </div>

      <div className="w-full border-t border-dashed border-gray-200 mb-4"></div>

      {/* Final Summary Area */}
      <div>
        {!isFormComplete && (
          <div className="flex items-center justify-center gap-2 bg-[#FFF6E9] border border-[#FDE5C0] text-[#E67F00] px-4 py-2.5 rounded-xl mb-4 text-[12.5px] font-bold">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Fill traveller details to continue
          </div>
        )}

        <div className="flex items-end justify-between mb-4">
          <div className="pb-1">
            <div className="text-[15px] font-black text-[#1a1a1a] mb-1">{paymentOption === "complete" ? "Total payable" : "Pay now"}</div>
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#21A5A0] uppercase tracking-wider">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              Secure Checkout
            </div>
          </div>
          <div className="text-[26px] font-black text-[#21A5A0] leading-none">
            ₹{dueNow.toLocaleString()}
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={timeLeft <= 0}
          className={`w-full py-4 rounded-2xl font-black text-[17px] transition-all flex items-center justify-center ${
            timeLeft > 0
              ? 'bg-[#E05A49] text-white hover:bg-[#c9483a] shadow-[0_4px_14px_rgba(224,90,73,0.4)] hover:shadow-[0_6px_20px_rgba(224,90,73,0.5)] transform hover:-translate-y-0.5'
              : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
          }`}
        >
          {timeLeft <= 0 ? 'Session Expired' : isFormComplete ? `Pay ₹${dueNow.toLocaleString()}` : 'Complete Form to Pay'}
        </button>

        <p className="text-[11px] text-[#888] text-center mt-3.5 font-medium px-2">
          By proceeding, you agree to our <a href="/terms_condition/" target="_blank" rel="noopener noreferrer" className="text-[#21A5A0] underline">Terms &amp; Conditions</a> and <a href="/privacy_policy/" target="_blank" rel="noopener noreferrer" className="text-[#21A5A0] underline">Privacy Policy</a>.
        </p>
      </div>

      {/* Sticky mobile pay bar: keeps the money action one tap away without
          scrolling. Shares handlePay/scrollToForm so there is a single source of
          truth for the payment logic. Portaled to body so no transformed
          ancestor can trap the fixed positioning. */}
      {typeof document !== "undefined" && createPortal(
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 z-[9998] bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 flex items-center gap-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="shrink-0">
            <div className="text-[10px] font-bold text-[#8a9793] uppercase tracking-wider leading-none mb-1">{paymentOption === "complete" ? "Total" : "Pay now"}</div>
            <div className="text-[20px] font-black text-[#21A5A0] leading-none">₹{dueNow.toLocaleString()}</div>
          </div>
          <button
            onClick={isFormComplete ? handlePay : scrollToForm}
            disabled={timeLeft <= 0}
            className={`flex-1 py-3.5 rounded-xl font-black text-[16px] transition-all ${
              timeLeft > 0
                ? 'bg-[#E05A49] text-white shadow-[0_4px_14px_rgba(224,90,73,0.4)] active:scale-[0.98]'
                : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
            }`}
          >
            {timeLeft <= 0 ? 'Session Expired' : isFormComplete ? 'Pay Now' : 'Enter Details'}
          </button>
        </div>,
        document.body
      )}
    </div>
  )
}

export default PaymentCard
